import { ActiveWork } from "../services/researchApi";
import {
  ResearchEntity,
  StageStatus,
  WorkflowState,
  WorkflowStatus,
} from "../types/researchWorkflow";

type Stages = WorkflowState["stages"];

/** Decision states shown on the Discovery board — keep in sync with LongListView. */
const LONG_LIST_STATUSES: ReadonlySet<WorkflowStatus> = new Set<WorkflowStatus>(
  ["candidate", "approved", "rejected", "final"],
);

/** Decision states shown on the Evaluation board — keep in sync with ShortListView. */
const SHORT_LIST_STATUSES: ReadonlySet<WorkflowStatus> =
  new Set<WorkflowStatus>(["approved", "shortlisted", "final"]);

/** The candidate's latest evaluation attempt is queued or running. */
export const isEvaluationActive = (entity: ResearchEntity): boolean =>
  entity.evaluationStatus === "queued" || entity.evaluationStatus === "running";

/**
 * The candidate's evaluation has settled: the latest attempt completed or
 * failed. A failure is (almost always) final from the user's point of view,
 * so it counts as evaluated rather than leaving the stage "awaiting"
 * forever; a *cancelled* attempt was the user's own "come back to this" and
 * stays awaiting. Results that predate per-candidate execution tracking
 * (#888) carry no status at all, so they fall back to the result's presence.
 */
export const isEvaluated = (entity: ResearchEntity): boolean =>
  entity.evaluationStatus === "completed" ||
  entity.evaluationStatus === "failed" ||
  ((entity.evaluationStatus ?? "none") === "none" &&
    entity.deepResearchStatus === "complete");

const evaluationStageStatus = (
  members: ResearchEntity[],
  activeWork: ActiveWork,
): StageStatus => {
  // Track-wide in-flight counts come from the activity poll and cover tasks
  // the entity list has not refreshed for yet; the per-candidate status
  // covers the reverse gap. Either means work is running.
  const trackBusy =
    activeWork.evaluation.queued + activeWork.evaluation.running > 0;
  if (trackBusy || members.some(isEvaluationActive)) return "in_progress";
  if (members.length === 0) return "not_started";
  if (members.every(isEvaluated)) return "complete";
  // Shortlisted candidates still waiting for an evaluation ("Awaiting
  // Evaluation" in the sidebar).
  return "ready";
};

/**
 * Derive the sidebar stage statuses and count badges from the entity list
 * and the track's in-flight work.
 *
 * Single source of truth for every place the hook recomputes stages (entity
 * refresh, activity poll, optimistic decision updates): the copies used to
 * drift — the Evaluation stage read "In Progress" whenever any candidate was
 * shortlisted, regardless of whether an evaluation had run, failed, or
 * finished, and the badges dropped `final` (and shortlist-only) candidates
 * that the boards themselves display.
 */
export const deriveStages = (
  entities: ResearchEntity[],
  activeWork: ActiveWork,
  prev: Stages,
): Stages => {
  const longList = entities.filter((e) =>
    LONG_LIST_STATUSES.has(e.workflowStatus),
  );
  const shortList = entities.filter((e) =>
    SHORT_LIST_STATUSES.has(e.workflowStatus),
  );
  // Approved-from-discovery includes candidates since promoted to final —
  // the Discovery board's own "approved" filter counts them the same way.
  const approvedCount = longList.filter(
    (e) => e.workflowStatus === "approved" || e.workflowStatus === "final",
  ).length;
  const rejectedCount = longList.filter(
    (e) => e.workflowStatus === "rejected",
  ).length;
  const finalCount = entities.filter(
    (e) => e.workflowStatus === "final",
  ).length;

  let longListStatus: StageStatus = "not_started";
  if (activeWork.discovery.length > 0) longListStatus = "in_progress";
  else if (entities.length > 0) longListStatus = "complete";

  return {
    ...prev,
    longList: {
      ...prev.longList,
      status: longListStatus,
      counts: {
        total: longList.length,
        approved: approvedCount,
        rejected: rejectedCount,
      },
    },
    shortList: {
      ...prev.shortList,
      status: evaluationStageStatus(shortList, activeWork),
      counts: {
        shortlisted: shortList.length,
        evaluated: shortList.filter(isEvaluated).length,
      },
    },
    finalSelection: {
      ...prev.finalSelection,
      status: finalCount > 0 ? "in_progress" : "not_started",
      counts: { final: finalCount },
    },
  };
};
